/**
 * Calendar utilities for generating calendar URLs and formatting dates
 */

export type CalendarType = "google" | "outlook" | "apple" | "ics";

/**
 * Format date for Google Calendar (YYYYMMDDTHHMMSS format)
 * Works best with the ctz parameter for timezone handling
 */
export function formatForGoogle(date: Date | null): string {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Format date for Outlook Calendar (ISO 8601 format)
 * Outlook expects YYYY-MM-DDTHH:MM:SSZ format
 */
export function formatForOutlook(date: Date | null): string {
  if (!date) return "";
  return date.toISOString();
}

/**
 * Generate attendee list for ICS format
 */
export function formatAttendeesForIcs(attendees: string[]): string {
  return attendees
    .map(
      (attendee) =>
        `ATTENDEE;CUTYPE=INDIVIDUAL;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE;X-NUM-GUESTS=0:mailto:${attendee}`,
    )
    .join("\r\n");
}

/**
 * Generate Google Calendar URL
 */
export function generateGoogleCalendarUrl({
  title,
  details,
  location,
  startDate,
  endDate,
  timezone,
  attendees,
}: {
  title: string;
  details: string;
  location: string;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  attendees: string[];
}): string {
  const googleParams = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    details,
    location,
    dates: `${formatForGoogle(startDate)}/${formatForGoogle(endDate)}`,
    ctz: timezone,
    add: attendees.join(","),
  });

  return `https://calendar.google.com/calendar/render?${googleParams}`;
}

/**
 * Generate Outlook Calendar URL
 */
export function generateOutlookCalendarUrl({
  title,
  body,
  location,
  startDate,
  endDate,
  attendees,
}: {
  title: string;
  body: string;
  location: string;
  startDate: Date | null;
  endDate: Date | null;
  attendees: string[];
}): string {
  const outlookParams = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: title,
    body,
    location,
    startdt: formatForOutlook(startDate) || "",
    enddt: formatForOutlook(endDate) || "",
    to: attendees.join(";"),
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${outlookParams}`;
}

/**
 * Generate ICS calendar content
 */
export function generateIcsCalendarContent({
  title,
  description,
  location,
  url,
  startDate,
  endDate,
  attendees,
}: {
  title: string;
  description: string;
  location: string;
  url: string;
  startDate: Date | null;
  endDate: Date | null;
  attendees: string[];
}): string {
  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `SUMMARY:${title}`,
    `DTSTART:${formatForGoogle(startDate)}`,
    `DTEND:${formatForGoogle(endDate)}`,
    `${formatAttendeesForIcs(attendees)}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    `URL:${url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n");

  return `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;
}

/**
 * Generate calendar data for all services
 */
export function generateAllCalendarData({
  title,
  description,
  meetingUrl,
  componentUrl,
  startDate,
  endDate,
  timezone,
  attendees,
  googleDetails,
  outlookDetails,
}: {
  title: string;
  description: string;
  meetingUrl: string;
  componentUrl: string;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  attendees: string[];
  googleDetails: string;
  outlookDetails: string;
}): Record<CalendarType, string> {
  return {
    google: generateGoogleCalendarUrl({
      title,
      details: googleDetails,
      location: meetingUrl,
      startDate,
      endDate,
      timezone,
      attendees,
    }),
    outlook: generateOutlookCalendarUrl({
      title,
      body: outlookDetails,
      location: meetingUrl,
      startDate,
      endDate,
      attendees,
    }),
    ics: generateIcsCalendarContent({
      title,
      description: `${description}\n\n🔗 Component Link:\n${componentUrl}`,
      location: componentUrl,
      url: componentUrl,
      startDate,
      endDate,
      attendees,
    }),
    apple: "", // Apple uses the same ICS format
  };
}
